import { useMutation } from "@tanstack/react-query";
import { FinancialAccount } from "@common/types/financialAccount";
import { Entity } from "@common/types/entity";
import { PlaidConnection } from "@common/types/plaidConnection";
import { MergeFinancialAccountsDto } from "@common/dto/financialAccount/mergeFinancialAccounts/merge-financial-accounts.dto";
import { financialAccountContractClient } from "@api/financialAccount.api";
import { invalidateAccountRelatedQueries } from "@api/account.api";
import { invalidateRuleApplicationQueries } from "@api/rules.api";
import { useScope } from "@state/scopeContext.lib";
import { useViewOnlyRefusal } from "@hooks/permissions/useViewOnlyRefusal";
import { FinancialAccountDto } from "@common/dto/financialAccount/financial-account.dto";
import {
    disableFinancialAccount,
    enableFinancialAccount,
    mergeFinancialAccounts,
} from "@lib/financialAccount";
import { invalidateLockQueries } from "@lib/lock";
import {
    entitiesAccountsQueryKey,
    getEntitiesAccountsQueryKey,
    removeFinancialAccountFromQueryData,
    updateFinancialAccountInQueryData,
} from "@queries/entitiesAccounts";
import {
    addUnassignedAccountInQueryData,
    removeUnassignedAccountFromQueryData,
} from "@queries/unassignedAccounts";
import { useFinancialAccounts } from "@hooks/useFinancialAccounts";
import { useConnectAccount } from "@components/connectAccount/ConnectAccount.context";
import {
    plaidConnectionsQueryKey,
    queryClient,
    financialConnectionsQueryKey,
} from "../queryClient";

function doFinancialAccountUpdate(
    account: FinancialAccount,
    update: (account: FinancialAccount) => void,
) {
    updateFinancialAccountInQueryData(queryClient, account);
    queryClient.setQueryData<PlaidConnection[] | undefined>(
        plaidConnectionsQueryKey,
        (connections) =>
            connections?.map((connection) => ({
                ...connection,
                accounts: connection.accounts?.map((a) => ({
                    ...a,
                    financialAccount:
                        a.financialAccount?.id === account.id
                            ? account
                            : a.financialAccount,
                })),
            })) ?? [],
    );
    if (account.entityId) {
        removeUnassignedAccountFromQueryData(queryClient, account.id);
    } else {
        addUnassignedAccountInQueryData(queryClient, account);
    }
    update(account);
}

function doFinancialAccountRemoval(account: FinancialAccount) {
    removeFinancialAccountFromQueryData(queryClient, account.id);
    removeUnassignedAccountFromQueryData(queryClient, account.id);
    queryClient.invalidateQueries({ queryKey: [financialConnectionsQueryKey] });
}

export function useChangeFinancialAccountEntityMutation(
    account: FinancialAccount,
    onSuccess?: () => void,
) {
    const { updateFinancialAccount } = useConnectAccount();
    const { workspaceId } = useScope();
    const { isViewOnly } = useViewOnlyRefusal();

    return useMutation<
        FinancialAccount,
        unknown,
        Entity,
        Entity | null | undefined
    >({
        mutationFn: async (entity) => {
            const response = await financialAccountContractClient.changeEntity({
                params: { financialAccountId: account.id },
                body: { entityId: entity.id },
            });

            return FinancialAccountDto.deserialize(response.body);
        },
        onMutate: async (entity) => {
            // View-only is refused by the API; an optimistic write would only
            // flash. `onError` already bails on an undefined snapshot.
            if (isViewOnly) {
                return;
            }
            const previousValue = account.entity;
            doFinancialAccountUpdate(
                {
                    ...account,
                    entity,
                    entityId: entity.id,
                    isBusiness: entity.isBusiness,
                },
                updateFinancialAccount,
            );

            return previousValue;
        },
        onSuccess: async (updatedAccount, _entity, previousEntity) => {
            doFinancialAccountUpdate(updatedAccount, updateFinancialAccount);
            onSuccess?.();

            const affectedEntityIds = [
                ...new Set(
                    [previousEntity?.id, updatedAccount.entityId].filter(
                        (id): id is number => typeof id === "number",
                    ),
                ),
            ];
            invalidateAccountRelatedQueries({
                entityIds: affectedEntityIds,
                workspaceId,
            });

            await queryClient.invalidateQueries({
                queryKey: [financialConnectionsQueryKey],
            });
            await queryClient.invalidateQueries({
                queryKey: getEntitiesAccountsQueryKey(
                    account.entity?.workspaceId ?? "",
                ),
            });
        },
        onError: (_1, _2, oldEntity) => {
            if (oldEntity !== undefined) {
                doFinancialAccountUpdate(
                    {
                        ...account,
                        entity: oldEntity,
                        entityId: oldEntity?.id ?? null,
                        isBusiness: oldEntity?.isBusiness ?? null,
                    },
                    updateFinancialAccount,
                );
            }
        },
    });
}

export function useFinancialAccountRemovalMutation(
    account: FinancialAccount,
    onRemoved?: () => void,
) {
    const { workspaceId } = useScope();

    return useMutation({
        mutationFn: ({ forceDeletion }: { forceDeletion?: boolean } = {}) =>
            financialAccountContractClient.deleteAccount({
                params: { financialAccountId: account.id },
                query: {
                    forceDeletion: forceDeletion ? "true" : undefined,
                },
            }),
        onSuccess: () => {
            doFinancialAccountRemoval(account);
            invalidateAccountRelatedQueries({
                entityIds: account.entityId ? [account.entityId] : undefined,
                workspaceId,
            });
            onRemoved?.();
        },
    });
}

export function useAccountEnablingMutation(account: FinancialAccount) {
    const { updateFinancialAccount } = useConnectAccount();

    return useMutation({
        mutationFn: () => enableFinancialAccount(account.id),
        onSuccess: (updatedAccount) => {
            doFinancialAccountUpdate(
                {
                    ...updatedAccount,
                    integrationAccounts: account.integrationAccounts,
                },
                updateFinancialAccount,
            );
            queryClient.invalidateQueries({
                queryKey: [financialConnectionsQueryKey],
            });
        },
    });
}

export function useAccountDisablingMutation(
    account: FinancialAccount,
    onDisabled?: () => void,
) {
    const { updateFinancialAccount } = useConnectAccount();

    return useMutation({
        mutationFn: () => disableFinancialAccount(account.id),
        onSuccess: (updatedAccount) => {
            doFinancialAccountUpdate(
                {
                    ...updatedAccount,
                    integrationAccounts: account.integrationAccounts,
                },
                updateFinancialAccount,
            );
            queryClient.invalidateQueries({
                queryKey: [financialConnectionsQueryKey],
            });
            onDisabled?.();
        },
    });
}

export function useAccountMergingMutation() {
    const accounts = useFinancialAccounts();
    const { workspaceId } = useScope();

    return useMutation({
        mutationFn: (data: MergeFinancialAccountsDto) =>
            mergeFinancialAccounts(data),

        onSuccess: (response, data) => {
            if (!data.dryRun && !response.conflictWhenMerging) {
                const account = accounts.find(
                    (a) => a.id === data.accountToMergeId,
                );
                if (account) {
                    doFinancialAccountRemoval(account);
                }
                queryClient.invalidateQueries({
                    queryKey: [entitiesAccountsQueryKey],
                });
                invalidateAccountRelatedQueries({
                    entityIds: account?.entityId
                        ? [account.entityId]
                        : undefined,
                    workspaceId,
                });
                if (workspaceId) {
                    void invalidateRuleApplicationQueries(workspaceId);
                }
            }
        },

        onSettled: (_response, _error, data) => {
            if (data.unlockPeriod) {
                invalidateLockQueries();
            }
        },
    });
}
